import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Input, Modal, Tabs, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocInlineDate,
  DocInlineAresName,
  DocInlineAresLookup,
  SavedDocsButton,
  NewDocButton,
  DraftBanner,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
  type DocRowsSnapshot,
} from '../../components/document';
import { MapPickerButton } from '../../components/map';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  type AresPickResult,
  type CompanyPickResult,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { useSaveDocument } from '../../api/hooks';
import {
  useKosilkyKeSchvaleni,
  useKosilkyOdeslane,
  useKosilkySchvalovatele,
  useCreateKosilkaSchvaleni,
  useDecideKosilkaSchvaleni,
  useCancelKosilkaSchvaleni,
} from '../../api/hooks';
import type { KosilkaSchvaleni } from '../../api/schemas';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useActiveStavba } from '../stavba-context/StavbaContext';

/**
 * Košilka SoD/objednávky — jednostránkový souhrn klíčových údajů ke smlouvě
 * o dílo nebo objednávce se subdodavatelem (vzor: „SoD/Objednávka — základní
 * informace"). Funguje jako „živý dokument" jako Objednávka: ARES našeptávač,
 * adresář firem, odeslání e-mailem, tisk/PDF.
 *
 * Navíc:
 *  — „Předat do objednávky": přenese vyplněné údaje do formuláře Objednávka.
 *  — „Odeslat ke schválení": pošle košilku kolegovi s účtem (našeptávač e-mailů),
 *    ten ji ve své aplikaci odsouhlasí / zamítne (záložka „Ke schválení").
 */

const viewTabs = [
  { id: 'kosilka', label: 'Košilka' },
  { id: 'schvaleni', label: 'Ke schválení' },
];

/** Klíč v sessionStorage pro přenos údajů z košilky do objednávky. */
export const KOSILKA_PREFILL_KEY = 'kosilka_to_objednavka';

interface KosilkaData {
  cisloAkce: string;
  cisloSmlouvy: string;
  predmet: string;
  nazevAkce: string;
  objednatel: { nazev: string; ico: string };
  partner: { nazev: string; ico: string; dic: string; sidlo: string };
  cenaInvestor: string;
  cenaDodavatel: string;
  terminOd: string;
  terminDo: string;
  zarukaMesice: string;
  zarukaOd: string;
  zarukaDo: string;
  zalohyPozadavek: string;
  vyseZaloh: string;
  splatnostZaloh: string;
  splatnost: string;
  zadrzne: string;
  pozastavka: string;
  podepsanoDne: string;
  podpisZpusob: string;
  poznamka: string;
  /** Skryté standardní řádky + vlastní řádky v hlavičkových sekcích. */
  radky?: DocRowsSnapshot;
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function deriveInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w && /[\p{L}]/u.test(w[0]) && !/^(s\.?r\.?o\.?|a\.?s\.?|spol|ltd|inc)$/i.test(w));
  if (words.length === 0) return '—';
  return words.slice(0, 3).map((w) => w[0].toLocaleUpperCase('cs')).join('');
}

export function KosilkaPage() {
  const [view, setView] = useState('kosilka');
  const keSchvaleni = useKosilkyKeSchvaleni();
  const pendingCount = (keSchvaleni.data ?? []).filter((z) => z.stav === 'ceka').length;

  const tabs = viewTabs.map((t) =>
    t.id === 'schvaleni' && pendingCount > 0 ? { ...t, label: `Ke schválení (${pendingCount})` } : t,
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 0 10px' }}>
        <Tabs items={tabs} value={view} onChange={setView} />
      </div>
      {view === 'kosilka' ? <KosilkaForm /> : <KeSchvaleni />}
    </>
  );
}

// ─── FORMULÁŘ KOŠILKY ────────────────────────────────────────────────────────

function KosilkaForm() {
  const saveDoc = useSaveDocument();
  const toast = useToast();
  const navigate = useNavigate();
  const { activeStavba } = useActiveStavba();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [schvOpen, setSchvOpen] = useState(false);

  const [cisloAkce, setCisloAkce] = useState('');
  const [cisloSmlouvy, setCisloSmlouvy] = useState('');
  const [predmet, setPredmet] = useState('');
  const [nazevAkce, setNazevAkce] = useState('');
  const [objednatel, setObjednatel] = useState({ nazev: '', ico: '' });
  const [partner, setPartner] = useState({ nazev: '', ico: '', dic: '', sidlo: '' });
  const [cenaInvestor, setCenaInvestor] = useState('');
  const [cenaDodavatel, setCenaDodavatel] = useState('');
  const [terminOd, setTerminOd] = useState('');
  const [terminDo, setTerminDo] = useState('');
  const [zarukaMesice, setZarukaMesice] = useState('');
  const [zarukaOd, setZarukaOd] = useState('');
  const [zarukaDo, setZarukaDo] = useState('');
  const [zalohyPozadavek, setZalohyPozadavek] = useState('');
  const [vyseZaloh, setVyseZaloh] = useState('');
  const [splatnostZaloh, setSplatnostZaloh] = useState('');
  const [splatnost, setSplatnost] = useState('');
  const [zadrzne, setZadrzne] = useState('');
  const [pozastavka, setPozastavka] = useState('');
  const [podepsanoDne, setPodepsanoDne] = useState('');
  const [podpisZpusob, setPodpisZpusob] = useState('');
  const [poznamka, setPoznamka] = useState('');

  const setObjPatch = (patch: Partial<typeof objednatel>) => setObjednatel((o) => ({ ...o, ...patch }));
  const setPartnerPatch = (patch: Partial<typeof partner>) => setPartner((p) => ({ ...p, ...patch }));
  // Skryté standardní řádky + vlastní řádky v hlavičkových sekcích (01–06).
  const rows = useDocRows();

  /** Předvyplní formulář ze snímku dat (dokument z archivu i lokální koncept). */
  function applySnapshot(d: Record<string, unknown>) {
    if (d.cisloAkce != null) setCisloAkce(String(d.cisloAkce));
    if (d.cisloSmlouvy != null) setCisloSmlouvy(String(d.cisloSmlouvy));
    if (d.predmet != null) setPredmet(String(d.predmet));
    if (d.nazevAkce != null) setNazevAkce(String(d.nazevAkce));
    if (d.objednatel) setObjednatel((o) => ({ ...o, ...(d.objednatel as Partial<typeof objednatel>) }));
    if (d.partner) setPartner((p) => ({ ...p, ...(d.partner as Partial<typeof partner>) }));
    if (d.cenaInvestor != null) setCenaInvestor(String(d.cenaInvestor));
    if (d.cenaDodavatel != null) setCenaDodavatel(String(d.cenaDodavatel));
    if (d.terminOd != null) setTerminOd(String(d.terminOd));
    if (d.terminDo != null) setTerminDo(String(d.terminDo));
    if (d.zarukaMesice != null) setZarukaMesice(String(d.zarukaMesice));
    if (d.zarukaOd != null) setZarukaOd(String(d.zarukaOd));
    if (d.zarukaDo != null) setZarukaDo(String(d.zarukaDo));
    if (d.zalohyPozadavek != null) setZalohyPozadavek(String(d.zalohyPozadavek));
    if (d.vyseZaloh != null) setVyseZaloh(String(d.vyseZaloh));
    if (d.splatnostZaloh != null) setSplatnostZaloh(String(d.splatnostZaloh));
    if (d.splatnost != null) setSplatnost(String(d.splatnost));
    if (d.zadrzne != null) setZadrzne(String(d.zadrzne));
    if (d.pozastavka != null) setPozastavka(String(d.pozastavka));
    if (d.podepsanoDne != null) setPodepsanoDne(String(d.podepsanoDne));
    if (d.podpisZpusob != null) setPodpisZpusob(String(d.podpisZpusob));
    if (d.poznamka != null) setPoznamka(String(d.poznamka));
    rows.apply(d.radky);
  }

  const { editId } = useDocEditor(applySnapshot);

  // Autosave rozpracovaného konceptu do prohlížeče (jen u nové košilky).
  const draft = useDocDraft<Record<string, unknown>>('kosilka', editId == null);
  function restoreDraft() {
    if (!draft.pending) return;
    applySnapshot(draft.pending.data);
    draft.accept();
  }
  useEffect(() => {
    draft.persist(buildData() as unknown as Record<string, unknown>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cisloAkce, cisloSmlouvy, predmet, nazevAkce, objednatel, partner, cenaInvestor,
    cenaDodavatel, terminOd, terminDo, zarukaMesice, zarukaOd, zarukaDo, zalohyPozadavek,
    vyseZaloh, splatnostZaloh, splatnost, zadrzne, pozastavka, podepsanoDne, podpisZpusob,
    poznamka, rows,
  ]);

  const docTitle = useMemo(() => {
    const akce = cisloAkce ? ` ${cisloAkce}` : '';
    return `Košilka${akce}${partner.nazev ? ' — ' + partner.nazev : ''}`;
  }, [cisloAkce, partner.nazev]);

  function buildData(): KosilkaData {
    return {
      cisloAkce, cisloSmlouvy, predmet, nazevAkce,
      objednatel, partner,
      cenaInvestor, cenaDodavatel,
      terminOd, terminDo, zarukaMesice, zarukaOd, zarukaDo,
      zalohyPozadavek, vyseZaloh, splatnostZaloh, splatnost, zadrzne, pozastavka,
      podepsanoDne, podpisZpusob, poznamka,
      radky: rows.snapshot(),
    };
  }

  /**
   * Uložení do archivu. Chybu ukáže toastem a POŠLE DÁL (throw) — volá ji i
   * dialog „Odeslat ke schválení", který bez uloženého id nesmí pokračovat.
   */
  async function handleSave(): Promise<number | null> {
    try {
      const res = await saveDoc.mutateAsync({
        id: editId ?? undefined,
        doc_type: 'kosilka',
        title: docTitle,
        stavba: nazevAkce || activeStavba?.nazev || '',
        firma: partner.nazev,
        data_json: buildData() as unknown as Record<string, unknown>,
      });
      draft.clear();
      toast.success('Košilka uložena do archivu.');
      return res?.id ?? editId ?? null;
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
      throw e;
    }
  }

  /** Přenese vyplněné údaje do formuláře Objednávka (přes sessionStorage). */
  function predatDoObjednavky() {
    const prefill = {
      cislo: cisloSmlouvy,
      dilo: predmet,
      terminZahajeni: terminOd,
      terminDokonceni: terminDo,
      stavba: { nazev: nazevAkce },
      obj: { nazev: objednatel.nazev, ico: objednatel.ico },
      dod: { nazev: partner.nazev, ico: partner.ico, dic: partner.dic, sidlo: partner.sidlo },
      polozky: [
        { id: newId('p'), popis: predmet || 'Dílo dle smlouvy', mnozstvi: '1', jednotka: 'kpl', cena: cenaDodavatel },
      ],
    };
    try {
      sessionStorage.setItem(KOSILKA_PREFILL_KEY, JSON.stringify(prefill));
    } catch {
      /* ignore */
    }
    navigate('/app/objednavka');
  }

  return (
    <>
      <DocLayout
        breadcrumb={['Obchod', 'Košilka']}
        title={docTitle}
        statusText="Rozpracovaný koncept"
        status="draft"
        actions={
          <>
            <NewDocButton draftKey="kosilka" path="/app/kosilka" />
            <SavedDocsButton
              docType="kosilka"
              title="Uložené košilky"
              currentId={editId}
            />
            <Button
              variant="secondary"
              leading="⎙"
              onClick={() => printToPdf(`Kosilka_${cisloAkce || 'koncept'}.pdf`)}
            >
              PDF
            </Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <Button variant="secondary" leading="→" onClick={predatDoObjednavky}>
              Předat do objednávky
            </Button>
            <Button variant="secondary" leading="✓" onClick={() => setSchvOpen(true)}>
              Odeslat ke schválení
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleSave().catch(() => undefined)}
              disabled={saveDoc.isPending}
            >
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        {draft.pending && (
          <DraftBanner
            savedAt={draft.pending.savedAt}
            onRestore={restoreDraft}
            onDiscard={draft.clear}
          />
        )}
        {editId != null && <OdeslaneStavBanner documentId={editId} />}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: deriveInitials(objednatel.nazev),
              name: objednatel.nazev || 'Objednatel',
              // Bez IČ nechceme v náhledu osamocenou pomlčku — necháme adresní řádek prázdný.
              address: objednatel.ico ? `IČ ${objednatel.ico}` : '',
            }}
            tag="Košilka"
            title={
              <>
                SoD / Objednávka{' '}
                <span style={{ fontWeight: 400, fontSize: 14, color: 'var(--sd-doc-dim)' }}>
                  · základní informace
                </span>
              </>
            }
            meta={[cisloAkce && `Akce ${cisloAkce}`, terminDo && `Termín do ${terminDo}`].filter(Boolean).join(' · ') || undefined}
            footer={{ left: objednatel.nazev || '—', center: 'košilka', right: cisloAkce || '—' }}
          >
            <DocBlock
              idx="01"
              title="Základní údaje"
              hiddenCount={rows.hiddenCount('s01')}
              onRestoreHidden={() => rows.restoreSection('s01')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s01" />
                  <StavbaPickerButton
                    title="Načíst stavbu ze seznamu"
                    onPick={(p: StavbaPickResult) => {
                      if (p.nazev) setNazevAkce(p.nazev);
                      if (p.zakazka_cislo) setCisloAkce(p.zakazka_cislo);
                    }}
                  />
                  <SaveStavbaButton
                    title="Uložit stavbu"
                    build={() => ({
                      nazev: nazevAkce,
                      adresa: '',
                      zakazka_cislo: cisloAkce,
                    })}
                  />
                </>
              }
            >
              {!rows.isHidden('s01.cislo') && (
                <DocRow k="Číslo akce" mono value={cisloAkce} onChange={setCisloAkce} placeholder="např. 2026/14" onRemove={() => rows.hide('s01.cislo')} />
              )}
              {!rows.isHidden('s01.smlouva') && (
                <DocRow k="Číslo smlouvy / objednávky" mono value={cisloSmlouvy} onChange={setCisloSmlouvy} placeholder="č. smlouvy nebo objednávky" onRemove={() => rows.hide('s01.smlouva')} />
              )}
              {!rows.isHidden('s01.predmet') && (
                <DocRow k="Předmět smlouvy" multiline rows={2} value={predmet} onChange={setPredmet} placeholder="např. zateplení fasády (ETICS), nátěr fasády…" onRemove={() => rows.hide('s01.predmet')} />
              )}
              {!rows.isHidden('s01.nazev') && (
                <DocRow k="Název akce" value={nazevAkce} onChange={setNazevAkce} placeholder="celý název stavby/akce" onRemove={() => rows.hide('s01.nazev')} />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Objednatel"
              hiddenCount={rows.hiddenCount('s02')}
              onRestoreHidden={() => rows.restoreSection('s02')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s02" />
                  <CompanyPickerButton
                    preferRole="objednatel"
                    title="Načíst objednatele z adresáře"
                    onPick={(c: CompanyPickResult) => setObjednatel({ nazev: c.nazev, ico: c.ico })}
                  />
                  <SaveCompanyButton
                    title="Uložit objednatele do adresáře"
                    build={() => ({ role: 'objednatel', nazev: objednatel.nazev, ico: objednatel.ico })}
                  />
                </>
              }
            >
              {!rows.isHidden('s02.nazev') && (
                <DocRow
                  k="Název"
                  v={
                    <DocInlineAresName
                      value={objednatel.nazev}
                      placeholder="např. KVS stavební, s.r.o."
                      onChange={(v) => setObjPatch({ nazev: v })}
                      onPick={(d: AresPickResult) => setObjPatch({ nazev: d.nazev, ico: d.ico })}
                    />
                  }
                  onRemove={() => rows.hide('s02.nazev')}
                />
              )}
              {!rows.isHidden('s02.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={objednatel.ico}
                  onChange={(v) => setObjPatch({ ico: v })}
                  placeholder="12345678"
                  actions={<DocInlineAresLookup ico={objednatel.ico} onResult={(d) => setObjPatch({ nazev: d.nazev, ico: d.ico })} />}
                  onRemove={() => rows.hide('s02.ico')}
                />
              )}
              <DocCustomRows rows={rows} section="s02" />
            </DocBlock>

            <DocBlock
              idx="03"
              title="Smluvní partner"
              hiddenCount={rows.hiddenCount('s03')}
              onRestoreHidden={() => rows.restoreSection('s03')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s03" />
                  <CompanyPickerButton
                    preferRole="subdodavatel"
                    title="Načíst smluvního partnera z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setPartner({ nazev: c.nazev, ico: c.ico, dic: c.dic, sidlo: c.adresa })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit smluvního partnera do adresáře"
                    build={() => ({
                      role: 'subdodavatel',
                      nazev: partner.nazev,
                      ico: partner.ico,
                      dic: partner.dic,
                      adresa: partner.sidlo,
                    })}
                  />
                </>
              }
            >
              {!rows.isHidden('s03.nazev') && (
                <DocRow
                  k="Název"
                  v={
                    <DocInlineAresName
                      value={partner.nazev}
                      placeholder="např. Stavby Novák s.r.o."
                      onChange={(v) => setPartnerPatch({ nazev: v })}
                      onPick={(d: AresPickResult) => setPartnerPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })}
                    />
                  }
                  onRemove={() => rows.hide('s03.nazev')}
                />
              )}
              {!rows.isHidden('s03.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={partner.ico}
                  onChange={(v) => setPartnerPatch({ ico: v })}
                  placeholder="12345678"
                  actions={<DocInlineAresLookup ico={partner.ico} onResult={(d) => setPartnerPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })} />}
                  onRemove={() => rows.hide('s03.ico')}
                />
              )}
              {!rows.isHidden('s03.dic') && (
                <DocRow k="DIČ" mono value={partner.dic} onChange={(v) => setPartnerPatch({ dic: v })} placeholder="CZ12345678" onRemove={() => rows.hide('s03.dic')} />
              )}
              {!rows.isHidden('s03.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={partner.sidlo}
                  onChange={(v) => setPartnerPatch({ sidlo: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={<MapPickerButton value={partner.sidlo} onApply={(v) => setPartnerPatch({ sidlo: v })} title="Sídlo partnera" label="Mapa" />}
                  onRemove={() => rows.hide('s03.sidlo')}
                />
              )}
              <DocCustomRows rows={rows} section="s03" />
            </DocBlock>

            <DocBlock
              idx="04"
              title="Cena díla (bez DPH)"
              hiddenCount={rows.hiddenCount('s04')}
              onRestoreHidden={() => rows.restoreSection('s04')}
              actions={<DocAddRowButton rows={rows} section="s04" />}
            >
              {!rows.isHidden('s04.investor') && (
                <DocRow k="Sml. cena s investorem" mono value={cenaInvestor} onChange={setCenaInvestor} placeholder="např. 1 200 000" onRemove={() => rows.hide('s04.investor')} />
              )}
              {!rows.isHidden('s04.dodavatel') && (
                <DocRow k="Sml. cena s dodavatelem" mono value={cenaDodavatel} onChange={setCenaDodavatel} placeholder="např. 480 000" onRemove={() => rows.hide('s04.dodavatel')} />
              )}
              <DocCustomRows rows={rows} section="s04" />
            </DocBlock>

            <DocBlock
              idx="05"
              title="Termíny a záruka"
              hiddenCount={rows.hiddenCount('s05')}
              onRestoreHidden={() => rows.restoreSection('s05')}
              actions={<DocAddRowButton rows={rows} section="s05" />}
            >
              {!rows.isHidden('s05.od') && (
                <DocRow k="Termín realizace od" mono value={terminOd} onChange={setTerminOd} placeholder="dd. mm. rrrr" actions={<DocInlineDate value={terminOd} onChange={setTerminOd} />} onRemove={() => rows.hide('s05.od')} />
              )}
              {!rows.isHidden('s05.do') && (
                <DocRow k="Termín realizace do" mono value={terminDo} onChange={setTerminDo} placeholder="dd. mm. rrrr" actions={<DocInlineDate value={terminDo} onChange={setTerminDo} />} onRemove={() => rows.hide('s05.do')} />
              )}
              {!rows.isHidden('s05.zaruka') && (
                <DocRow k="Záruční doba (měsíců)" mono value={zarukaMesice} onChange={setZarukaMesice} placeholder="např. 62" onRemove={() => rows.hide('s05.zaruka')} />
              )}
              {!rows.isHidden('s05.zarukaOd') && (
                <DocRow k="Začátek záruční doby" mono value={zarukaOd} onChange={setZarukaOd} placeholder="dd. mm. rrrr" actions={<DocInlineDate value={zarukaOd} onChange={setZarukaOd} />} onRemove={() => rows.hide('s05.zarukaOd')} />
              )}
              {!rows.isHidden('s05.zarukaDo') && (
                <DocRow k="Konec záruční doby" mono value={zarukaDo} onChange={setZarukaDo} placeholder="dd. mm. rrrr" actions={<DocInlineDate value={zarukaDo} onChange={setZarukaDo} />} onRemove={() => rows.hide('s05.zarukaDo')} />
              )}
              <DocCustomRows rows={rows} section="s05" />
            </DocBlock>

            <DocBlock
              idx="06"
              title="Zálohy a platební podmínky"
              hiddenCount={rows.hiddenCount('s06')}
              onRestoreHidden={() => rows.restoreSection('s06')}
              actions={<DocAddRowButton rows={rows} section="s06" />}
            >
              {!rows.isHidden('s06.zalohy') && (
                <DocRow k="Zálohové faktury" value={zalohyPozadavek} onChange={setZalohyPozadavek} placeholder="ANO / NE" onRemove={() => rows.hide('s06.zalohy')} />
              )}
              {!rows.isHidden('s06.vyse') && (
                <DocRow k="Výše záloh" value={vyseZaloh} onChange={setVyseZaloh} placeholder="např. 100 000 Kč po 14 dnech práce" onRemove={() => rows.hide('s06.vyse')} />
              )}
              {!rows.isHidden('s06.splatnostZaloh') && (
                <DocRow k="Splatnost zálohových faktur" mono value={splatnostZaloh} onChange={setSplatnostZaloh} placeholder="např. 7 dní" onRemove={() => rows.hide('s06.splatnostZaloh')} />
              )}
              {!rows.isHidden('s06.splatnost') && (
                <DocRow k="Splatnost (dní)" mono value={splatnost} onChange={setSplatnost} placeholder="např. 21" onRemove={() => rows.hide('s06.splatnost')} />
              )}
              {!rows.isHidden('s06.zadrzne') && (
                <DocRow k="Zádržné" mono value={zadrzne} onChange={setZadrzne} placeholder="např. 0 %" onRemove={() => rows.hide('s06.zadrzne')} />
              )}
              {!rows.isHidden('s06.pozastavka') && (
                <DocRow k="Pozastávka" mono value={pozastavka} onChange={setPozastavka} placeholder="např. 0 %" onRemove={() => rows.hide('s06.pozastavka')} />
              )}
              <DocCustomRows rows={rows} section="s06" />
            </DocBlock>

            <DocBlock idx="07" title="Podpis a poznámka">
              <DocRow k="Smlouva podepsána dne" mono value={podepsanoDne} onChange={setPodepsanoDne} placeholder="dd. mm. rrrr" actions={<DocInlineDate value={podepsanoDne} onChange={setPodepsanoDne} />} />
              <DocRow k="Způsob podpisu" value={podpisZpusob} onChange={setPodpisZpusob} placeholder="elektronicky / ručně" />
              <DocRow k="Poznámka" multiline rows={2} value={poznamka} onChange={setPoznamka} placeholder="doplňující poznámka" />
            </DocBlock>
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="kosilka"
        docTitle={docTitle}
        defaultSubject={docTitle}
        meta={`Smluvní partner: ${partner.nazev || '—'}${nazevAkce ? ' · ' + nazevAkce : ''}`}
      />

      <SchvaleniModal
        open={schvOpen}
        onClose={() => setSchvOpen(false)}
        title={docTitle}
        stavba={nazevAkce || activeStavba?.nazev || ''}
        buildData={() => buildData() as unknown as Record<string, unknown>}
        saveDoc={handleSave}
        onSaved={(id) => {
          // Po odeslání přepni do režimu úprav uloženého dokumentu (?id) — ukáže se
          // stavový banner a další „Uložit" přepíše tentýž záznam (žádný duplikát).
          if (id != null && editId == null) navigate(`/app/kosilka?id=${id}`, { replace: true });
        }}
      />
    </>
  );
}

// ─── ODESLAT KE SCHVÁLENÍ (modal) ────────────────────────────────────────────

function SchvaleniModal({
  open, onClose, title, stavba, buildData, saveDoc, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  stavba: string;
  buildData: () => Record<string, unknown>;
  saveDoc: () => Promise<number | null>;
  onSaved?: (documentId: number | null) => void;
}) {
  const create = useCreateKosilkaSchvaleni();
  const { data: schvalovatele = [] } = useKosilkySchvalovatele();
  const [email, setEmail] = useState('');
  const [zprava, setZprava] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function odeslat() {
    const e = email.trim();
    if (!e) return;
    setErr(null);
    try {
      // Nejdřív košilku ulož (do archivu + získej id), pak pošli žádost se snapshotem.
      const documentId = await saveDoc();
      await create.mutateAsync({
        email: e,
        title,
        stavba,
        data_json: buildData(),
        zprava: zprava.trim(),
        document_id: documentId,
      });
      setDone(true);
      onSaved?.(documentId);
    } catch (ex: unknown) {
      const status = (ex as { status?: number })?.status;
      setErr(
        status === 404 ? 'Uživatel s tímto e-mailem nenalezen. Schvalovatel musí mít účet v aplikaci.'
          : status === 400 ? 'Košilku nelze poslat ke schválení sám sobě.'
          : 'Odeslání se nezdařilo.',
      );
    }
  }

  function close() {
    setEmail('');
    setZprava('');
    setErr(null);
    setDone(false);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Odeslat košilku ke schválení"
      size="md"
      footer={
        done ? (
          <Button variant="primary" onClick={close}>Hotovo</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>Zrušit</Button>
            <Button variant="primary" onClick={odeslat} disabled={create.isPending || !email.trim()}>
              {create.isPending ? 'Odesílám…' : '✓ Odeslat ke schválení'}
            </Button>
          </>
        )
      }
    >
      {done ? (
        <div style={{ padding: '10px 12px', borderRadius: 4, background: 'var(--sd-success-soft, #e7f6ec)', color: 'var(--sd-success, #1f8b4c)', fontSize: 13, fontWeight: 600 }}>
          ✓ Košilka byla odeslána ke schválení na <strong>{email.trim()}</strong>. Stav uvidíte na formuláři i v přehledu odeslaných.
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 12, fontSize: 12.5, color: 'var(--sd-text-dim)' }}>
            Košilku dostane ke schválení kolega podle e-mailu (musí mít účet v aplikaci). Uvidí ji ve své aplikaci v záložce „Ke schválení" a klikne Odsouhlasit / Zamítnout. Schvalovatel uvidí přesně tu verzi, kterou teď odešlete.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>E-mail schvalovatele *
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e-mail kolegy"
                list="kosilka-schvalovatele"
              />
              <datalist id="kosilka-schvalovatele">
                {schvalovatele.map((s) => (
                  <option key={s.email} value={s.email}>{s.name ? `${s.name} · ${s.email}` : s.email}</option>
                ))}
              </datalist>
            </label>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)' }}>Zpráva (nepovinné)
              <textarea
                value={zprava}
                onChange={(e) => setZprava(e.target.value)}
                rows={3}
                placeholder="např. Prosím o odsouhlasení před vystavením objednávky."
                style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', marginTop: 4 }}
              />
            </label>
          </div>
          {err && <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--sd-danger)' }}>{err}</div>}
        </>
      )}
    </Modal>
  );
}

// ─── STAV ODESLANÉ ŽÁDOSTI (banner na formuláři) ─────────────────────────────

function OdeslaneStavBanner({ documentId }: { documentId: number }) {
  const { data: odeslane = [] } = useKosilkyOdeslane();
  const cancel = useCancelKosilkaSchvaleni();
  const zadosti = odeslane.filter((z) => z.document_id === documentId);
  if (zadosti.length === 0) return null;

  return (
    <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {zadosti.map((z) => (
        <div key={z.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '10px 14px', borderRadius: 6, border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', background: '#fff' }}>
          <div style={{ fontSize: 13 }}>
            <StavBadge stav={z.stav} />{' '}
            <span style={{ color: 'var(--sd-text-dim)' }}>
              schvaluje <strong style={{ color: 'var(--sd-doc-ink)' }}>{z.approver_name || z.approver_email}</strong>
              {z.komentar && z.stav !== 'ceka' && <> · „{z.komentar}"</>}
            </span>
          </div>
          {z.stav === 'ceka' && (
            <Button size="sm" variant="ghost" onClick={() => cancel.mutate(z.id)} disabled={cancel.isPending}>
              Zrušit žádost
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

function StavBadge({ stav }: { stav: KosilkaSchvaleni['stav'] }) {
  if (stav === 'odsouhlaseno') return <Badge tone="success" pill dot>✓ Odsouhlaseno</Badge>;
  if (stav === 'zamitnuto') return <Badge tone="danger" pill dot>Zamítnuto</Badge>;
  return <Badge tone="neutral" pill>Čeká na schválení</Badge>;
}

// ─── ZÁLOŽKA „KE SCHVÁLENÍ" (schvalovatel) ───────────────────────────────────

const SNAPSHOT_ROWS: { key: keyof KosilkaData | string; label: string }[] = [
  { key: 'predmet', label: 'Předmět' },
  { key: 'cenaDodavatel', label: 'Cena s dodavatelem (bez DPH)' },
  { key: 'cenaInvestor', label: 'Cena s investorem (bez DPH)' },
  { key: 'terminOd', label: 'Termín od' },
  { key: 'terminDo', label: 'Termín do' },
  { key: 'splatnost', label: 'Splatnost (dní)' },
];

function KeSchvaleni() {
  const { data: zadosti = [], isLoading, error } = useKosilkyKeSchvaleni();
  const decide = useDecideKosilkaSchvaleni();
  const [reject, setReject] = useState<KosilkaSchvaleni | null>(null);

  if (isLoading) return <div style={{ padding: 20, color: 'var(--sd-text-dim)' }}>Načítám…</div>;
  if (error) return <div style={{ padding: 20, color: 'var(--sd-danger)' }}>Chyba načítání.</div>;
  if (zadosti.length === 0) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--sd-text-dim)' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--sd-doc-ink)' }}>Žádné košilky ke schválení</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>Košilky, které vám kolegové pošlou ke schválení, se objeví zde.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {zadosti.map((z) => {
        const data = (z.data_json ?? {}) as Record<string, string>;
        const partner = (data.partner ?? {}) as unknown as { nazev?: string; ico?: string };
        const rozhodnuto = z.stav !== 'ceka';
        return (
          <div key={z.id} style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 6, background: '#fff', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--sd-w-efeae0, #efeae0)', background: 'var(--sd-w-f7f4ee, #f7f4ee)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--sd-doc-ink)' }}>{z.title || 'Košilka SoD/objednávky'}</div>
                <div style={{ fontSize: 12.5, color: 'var(--sd-text-dim)', marginTop: 2 }}>
                  od <strong>{z.owner_name || z.owner_email}</strong>
                  {z.stavba && <> · {z.stavba}</>}
                </div>
              </div>
              <StavBadge stav={z.stav} />
            </div>

            <div style={{ padding: '12px 16px' }}>
              {z.zprava && (
                <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 4, background: 'var(--sd-yellow-soft, #fdf6e3)', fontSize: 13, color: 'var(--sd-doc-ink)' }}>
                  „{z.zprava}"
                </div>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  <tr>
                    <td style={snapKey}>Smluvní partner</td>
                    <td style={snapVal}>{partner.nazev || '—'}{partner.ico ? ` · IČ ${partner.ico}` : ''}</td>
                  </tr>
                  {SNAPSHOT_ROWS.map((r) => (
                    <tr key={r.key as string}>
                      <td style={snapKey}>{r.label}</td>
                      <td style={snapVal}>{data[r.key as string] || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {rozhodnuto ? (
                <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--sd-text-dim)' }}>
                  {z.stav === 'odsouhlaseno' ? 'Odsouhlaseno' : 'Zamítnuto'}
                  {z.komentar && <> · „{z.komentar}"</>}
                </div>
              ) : (
                <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button variant="ghost" onClick={() => setReject(z)} disabled={decide.isPending}>Zamítnout</Button>
                  <Button variant="primary" onClick={() => decide.mutate({ id: z.id, stav: 'odsouhlaseno' })} disabled={decide.isPending}>
                    ✓ Odsouhlasit
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <ZamitnoutModal
        zadost={reject}
        pending={decide.isPending}
        onClose={() => setReject(null)}
        onSubmit={(komentar) => {
          if (reject) decide.mutate({ id: reject.id, stav: 'zamitnuto', komentar });
          setReject(null);
        }}
      />
    </div>
  );
}

function ZamitnoutModal({
  zadost, pending, onClose, onSubmit,
}: {
  zadost: KosilkaSchvaleni | null;
  pending: boolean;
  onClose: () => void;
  onSubmit: (komentar: string) => void;
}) {
  const [text, setText] = useState('');
  const open = !!zadost;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Zamítnout košilku"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Zpět</Button>
          <Button variant="danger" disabled={pending} onClick={() => { onSubmit(text.trim()); setText(''); }}>
            {pending ? 'Odesílám…' : 'Zamítnout'}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: 'var(--sd-text-dim)', marginBottom: 10 }}>
        Napište prosím důvod zamítnutí — uvidí ho odesílatel.
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="Důvod zamítnutí…"
        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
      />
    </Modal>
  );
}

const snapKey: React.CSSProperties = { padding: '5px 10px 5px 0', color: 'var(--sd-text-dim)', width: 220, verticalAlign: 'top', fontSize: 12.5 };
const snapVal: React.CSSProperties = { padding: '5px 0', color: 'var(--sd-doc-ink)', fontWeight: 600 };
